import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { GestorResponse } from '../interfaces/gestores.interface';
import { environment } from 'src/environments/environment';
import { SessionService } from '@shared/helpers/sesion.service';
import { IGetDepto_Ciudad, IGetDepto_CiudadRes, IDeptoCiu } from '@p-p-portafolio/promociones/interfaces/IGetDepto_Ciudad';

interface Agencia {
  susuario: string;
  nombreusuario: string;
}

interface Clave {
  valor: string;
  descripcion: string;
}

interface Empresa {
  sEmpresa: string;
  sNombre: string;
  cuentaContable: string;
  responsable: string;
  calle: string;
  colonia: string;
  ciudad: string;
  del_Mun: string;
  cve_Estado: string;
  cp: number;
  telefono: string;
  sTipo: string;
}

interface Sucursal {
  sEmpresa: string;
  sSucursal: string;
  nombre_Sucursal: string;
  ciudad: string;
}

interface Agenda {
  sOpcion: string;
  sValor: string;
}

interface AgendaItem {
  iagenda: number;
  sopcion: string;
  iprioridad: number;
}

@Injectable({
  providedIn: 'root'
})
export class GestoresService {
  private apiUrl = environment.apiNewBackBaseUrl;

  constructor(private http: HttpClient, private sessionService: SessionService) { }

  getGestores(): Observable<GestorResponse> {
    return this.http.post<GestorResponse>(`${this.apiUrl}Agencias/cmspa_agenciasList`, {
      que: "4",
      tope: "9999",
      cmd: "",
      sAgencia: "",
      sNombre: "",
      clave: "",
      tipo: "",
      nombre_titular: "",
      calle: "",
      numero_ext: "",
      numero_int: "",
      colonia: "",
      ciudad: "",
      del_muni: "",
      cve_estado: "",
      cp: 0,
      rfc: "",
      telefono: "",
      telefono2: "",
      fax: "",
      supervisor: "",
      zona: "",
      claveCd: "",
      sEmpresa: "",
      sucursal: "",
      producto: "",
      codigoInterno: "",
      diasEspera: 0,
      grupoEmpresa: ""
    });
  }

  getGestorById(sAgencia: string): Observable<{success: boolean, data: any[]}> {
    return this.http.post<{success: boolean, data: any[]}>(`${this.apiUrl}Gestores/cmspa_agenciasListDet`, {
      que: "5",
      tope: "",
      cmd: "",
      sAgencia: sAgencia,
      sNombre: "",
      clave: "",
      tipo: "",
      nombre_titular: "",
      calle: "",
      numero_ext: "",
      numero_int: "",
      colonia: "",
      ciudad: "",
      del_muni: "",
      cve_estado: "",
      cp: 0,
      rfc: "",
      telefono: "",
      telefono2: "",
      fax: "",
      supervisor: "",
      zona: "",
      claveCd: "",
      sEmpresa: "",
      sucursal: "",
      producto: "",
      codigoInterno: "",
      diasEspera: 0,
      grupoEmpresa: ""
    });
  }

  deleteGestor(sAgencia: string): Observable<{ success: boolean; data: boolean }> {
    return this.http.post<{ success: boolean; data: boolean }>(`${this.apiUrl}Gestores/cmspa_agenciasDelete`, {
      que: "3",
      tope: "",
      cmd: "",
      sAgencia: sAgencia,
      sNombre: "",
      clave: "",
      tipo: "",
      nombre_titular: "",
      calle: "",
      numero_ext: "",
      numero_int: "",
      colonia: "",
      ciudad: "",
      del_muni: "",
      cve_estado: "",
      cp: 0,
      rfc: "",
      telefono: "",
      telefono2: "",
      fax: "",
      supervisor: "",
      zona: "",
      claveCd: "",
      sEmpresa: "",
      sucursal: "",
      producto: "",
      codigoInterno: "",
      diasEspera: 0,
      grupoEmpresa: ""
    });
  }

  getAgencias(): Observable<Agencia[]> {
    return this.http.post<{success: boolean, data: Agencia[]}>(`${this.apiUrl}UsuarsCS/Getcmspa_usua4V2`, {
      tope: "9999",
      usuario: ""
    }).pipe(
      map(response => response.data)
    );
  }

  getClaves(): Observable<Clave[]> {
    return this.http.post<{success: boolean, data: {sOpcion: string, sValor: string}[]}>(`${this.apiUrl}CentrodePagos/GetPvtTwhrA7`, {
      iPipe: 0,
      sUsuario: this.sessionService.getUserSession(),
      iIdioma: 0
    }).pipe(
      map(response => response.data.map(item => ({
        valor: item.sValor,
        descripcion: item.sOpcion
      })))
    );
  }

  getDepartamentos(): Observable<IDeptoCiu[]> {
    const payload: IGetDepto_Ciudad = { identificador: 1, valor: "" };
    return this.http.post<IGetDepto_CiudadRes>(`${this.apiUrl}DeptoCiudad/GetDepto_Ciudad`, payload).pipe(
      map(response => response.data)
    );
  }

  getCiudades(idDepartamento: string): Observable<IDeptoCiu[]> {
    const payload: IGetDepto_Ciudad = { identificador: 2, valor: idDepartamento };
    return this.http.post<IGetDepto_CiudadRes>(`${this.apiUrl}DeptoCiudad/GetDepto_Ciudad`, payload).pipe(
      map(response => response.data)
    );
  }

  getEmpresas(): Observable<Empresa[]> {
    return this.http.post<{success: boolean, data: Empresa[]}>(`${this.apiUrl}GtEmpresas/GetEmpresas`, {}).pipe(
      map(response => response.data)
    );
  }

  getRegionales(): Observable<Clave[]> {
    return this.http.post<{success: boolean, data: Clave[]}>(`${this.apiUrl}ColasDeTrabajo/GetOpcionColasDeTrabajo`, {
      sTabla: "empresas",
      sCampo: "snombre,sempresa",
      sWhere: "where CuentaContable= 'Grupo'",
      iPipe: 0,
      sUsuario: this.sessionService.getUserSession(),
      iIdioma: 0
    }).pipe(
      map(response => response.data)
    );
  }

  getTipos(): Observable<Clave[]> {
    return this.http.post<{success: boolean, data: {sOpcion: string, sValor: string}[]}>(`${this.apiUrl}CentrodePagos/GetPvtTwhrA8`, {
      iPipe: 0,
      sUsuario: this.sessionService.getUserSession(),
      iIdioma: 0
    }).pipe(
      map(response => response.data.map(item => ({
        valor: item.sValor,
        descripcion: item.sOpcion
      })))
    );
  }

  getSupervisores(): Observable<any[]> {
    return this.http.post<{success: boolean, data: any[]}>(`${this.apiUrl}Agencias/cmspa_agenciasList`, {
      que: "4",
      tope: "9999",
      cmd: "",
      sAgencia: "0",
      sNombre: "",
      clave: "",
      tipo: "",
      nombre_titular: "",
      calle: "",
      numero_ext: "",
      numero_int: "",
      colonia: "",
      ciudad: "",
      del_muni: "",
      cve_estado: "",
      cp: 0,
      rfc: "",
      telefono: "",
      telefono2: "",
      fax: "",
      supervisor: "",
      zona: "",
      claveCd: "",
      sEmpresa: "",
      sucursal: "",
      producto: "",
      codigoInterno: "",
      diasEspera: 0,
      grupoEmpresa: ""
    }).pipe(
      map(response => response.data)
    );
  }

  getSucursales(empresa: string): Observable<Sucursal[]> {
    return this.http.post<{success: boolean, data: Sucursal[]}>(`${this.apiUrl}CampoTablaWhere/GetSucursales`, {
      empresa: empresa
    }).pipe(
      map(response => response.data)
    );
  }

  getAgendas(): Observable<Agenda[]> {
    return this.http.post<{success: boolean, data: Agenda[]}>(`${this.apiUrl}OpcionesSolicitud/GetOpcionesVariables`, {
      sTabla: "citbl_prioridadagenda",
      sCampo: "iagenda",
      iIdioma: 0
    }).pipe(
      map(response => response.data)
    );
  }

  saveAgenda(agendaData: any): Observable<{success: boolean, data: boolean}> {
    return this.http.post<{success: boolean, data: boolean}>(`${this.apiUrl}AgendasControllers/cispa_prioridadagendaAdd`, {
      sUsuario: this.sessionService.getUserSession(),
      iIdioma: 0,
      iAccion: 0,
      sDeUsuario: agendaData.sDeUsuario,
      iAgenda: agendaData.iAgenda,
      iPrioridad: agendaData.iPrioridad
    });
  }

  getAgendaItems(sDeUsuario: string): Observable<AgendaItem[]> {
    return this.http.post<{success: boolean, data: AgendaItem[]}>(`${this.apiUrl}AgendasControllers/cispa_prioridadagendaList`, {
      sUsuario: this.sessionService.getUserSession(),
      iIdioma: 0,
      iAccion: 4,
      sDeUsuario: sDeUsuario,
      iAgenda: 0,
      iPrioridad: 0
    }).pipe(
      map(response => response.data)
    );
  }

  deleteAgenda(agendaData: any): Observable<{success: boolean, data: boolean}> {
    return this.http.post<{success: boolean, data: boolean}>(`${this.apiUrl}AgendasControllers/cispa_prioridadagendaDelete`, {
      sUsuario: this.sessionService.getUserSession(),
      iIdioma: 0,
      iAccion: 3,
      sDeUsuario: agendaData.sDeUsuario,
      iAgenda: agendaData.iAgenda,
      iPrioridad: agendaData.iPrioridad
    });
  }

  updateAgenda(agendaData: any): Observable<{success: boolean, data: boolean}> {
    return this.http.post<{success: boolean, data: boolean}>(`${this.apiUrl}AgendasControllers/cispa_prioridadagendaUpd`, {
      sUsuario: this.sessionService.getUserSession(),
      iIdioma: 0,
      iAccion: 1,
      sDeUsuario: agendaData.sDeUsuario,
      iAgenda: agendaData.iAgenda,
      iPrioridad: agendaData.iPrioridad
    });
  }

  createGestor(gestor: any): Observable<{success: boolean, data: boolean}> {
    return this.http.post<{success: boolean, data: boolean}>(`${this.apiUrl}Gestores/cmspa_agenciasAdd`, {
      que: "1",
      tope: "",
      cmd: "",
      sAgencia: gestor.sAgencia,
      sNombre: gestor.sNombre,
      clave: gestor.clave,
      tipo: gestor.tipo,
      nombre_titular: gestor.nombre_titular,
      calle: gestor.calle,
      numero_ext: gestor.numero_ext,
      numero_int: gestor.numero_int,
      colonia: gestor.colonia,
      ciudad: gestor.ciudad,
      del_muni: gestor.del_muni,
      cve_estado: gestor.cve_estado,
      cp: gestor.cp,
      rfc: gestor.rfc,
      telefono: gestor.telefono,
      telefono2: gestor.telefono2,
      fax: gestor.fax,
      supervisor: gestor.supervisor,
      zona: "",
      claveCd: "",
      sEmpresa: gestor.sEmpresa,
      sucursal: gestor.sSucursal,
      producto: "",
      codigoInterno: gestor.codigoInterno?.toString() || "",
      diasEspera: gestor.diasEspera,
      grupoEmpresa: ""
    });
  }

  updateGestor(gestor: any): Observable<{success: boolean, data: boolean}> {
    return this.http.post<{success: boolean, data: boolean}>(`${this.apiUrl}Gestores/cmspa_agenciasUpd`, {
      que: "2",
      tope: "",
      cmd: "",
      sAgencia: gestor.sAgencia,
      sNombre: gestor.sNombre,
      clave: gestor.clave,
      tipo: gestor.tipo,
      nombre_titular: gestor.nombre_titular,
      calle: gestor.calle,
      numero_ext: gestor.numero_ext,
      numero_int: gestor.numero_int,
      colonia: gestor.colonia,
      ciudad: gestor.ciudad,
      del_muni: gestor.del_muni,
      cve_estado: gestor.cve_estado,
      cp: gestor.cp,
      rfc: gestor.rfc,
      telefono: gestor.telefono,
      telefono2: gestor.telefono2,
      fax: gestor.fax,
      supervisor: gestor.supervisor,
      zona: "",
      claveCd: "",
      sEmpresa: gestor.sEmpresa,
      sucursal: gestor.sSucursal,
      producto: "",
      codigoInterno: gestor.codigoInterno?.toString() || "",
      diasEspera: gestor.diasEspera,
      grupoEmpresa: ""
    });
  }
} 